import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  FlatList,
  Modal,
  PanResponder,
} from "react-native";
import React, { useState, useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";
import { Entypo, FontAwesome } from "@expo/vector-icons";
import { Ionicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";
import AddCalendarBlockView from "../../Block/AddCalendarBlockView";
import MacroDescriptionView from "./MacroDescriptionView";
import {
  endOfWeek,
  formatDayMonth,
  getMondayOfWeek as getMondayOfWeekDateUtils,
  getSundayOfWeek,
} from "../../utils/dateUtils";
import { getEmptyImage } from "react-dnd-html5-backend";
import ScrollButtons from "../../Components/ScrollButtons";
import { Calendar } from "../../../Resources/Constants";
import MacroRowView from "./CalendarMacroRowView";
import CalendarBlockRowView from "./CalendarBlockRowView";
import IconEmptyView from "../../Components/IconEmptyView";
import { shouldShowProtocolSlotUi } from "../../platform/guards/coachBlockDashboardVisibility";

const daysOfWeek = [
  "monday-string",
  "tuesday-string",
  "wednesday-string",
  "thursday-string",
  "friday-string",
  "saturday-string",
  "sunday-string",
];
const monthNames = [
  "january-string",
  "february-string",
  "march-string",
  "april-string",
  "may-string",
  "june-string",
  "july-string",
  "august-string",
  "september-string",
  "october-string",
  "november-string",
  "december-string",
];

const WeekCalendar = observer(({ client }) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const [showingAddCalendarBlock, setShowingAddCalendarBlock] = useState(false);

  const [selectionStartWeek, setSelectionStartWeek] = useState(null);
  const [selectionEndWeek, setSelectionEndWeek] = useState(null);
  const [selectionStartDate, setSelectionStartDate] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  // Use selection states directly for the modal
  // Immediate selection refs to avoid state timing issues
  const isSelectingRef = useRef(false);
  const selectionStartRef = useRef(null);
  const selectionEndRef = useRef(null);
  const MAX_SELECT_WEEKS = 24;
  const gridRef = useRef(null);
  const openModalTimeoutRef = useRef(null);

  // Helper to clear selection
  const clearSelection = React.useCallback(() => {
    setSelectionStartDate(null);
    setSelectionStartWeek(null);
    setSelectionEndWeek(null);
    setIsSelecting(false);
  }, []);

  const year = client.weekCalendarYear;

  // Memoized calendar data and month headers
  const generateCalendarData = (year) => {
    const startOfYear = new Date(year, 0, 1, 12); // local noon
    const getStartDayOfYear = () => {
      // Map JS Sunday=0..Saturday=6 to Monday=0..Sunday=6
      const d = new Date(year, 0, 1, 12);
      const js = d.getDay(); // 0..6
      return js === 0 ? 6 : js - 1;
    };

    const startDay = getStartDayOfYear(); // 0 = Monday … 6 = Sunday
    const weeks = [];
    let currentWeek = Array(7).fill(null);
    let currentDate = startOfYear;

    // Fill first (possibly partial) week
    for (let i = startDay; i < 7; i++) {
      currentWeek[i] = new Date(currentDate);
      currentDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 1,
        12
      );
    }
    weeks.push(currentWeek);

    // Fill subsequent weeks
    while (currentDate.getFullYear() === year) {
      currentWeek = Array(7).fill(null);
      for (let i = 0; i < 7 && currentDate.getFullYear() === year; i++) {
        currentWeek[i] = new Date(currentDate);
        currentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate() + 1,
          12
        );
      }
      weeks.push(currentWeek);
    }
    return weeks;
  };

  const getMonthHeaders = (weeks) => {
    // Align headers to the month that the week's Monday belongs to.
    // Each week column is one week; header span counts contiguous weeks with same Monday's month.
    const monthHeaders = [];
    if (!weeks || weeks.length === 0) return monthHeaders;

    const getMonday = (week) => {
      // Return Monday date for a week array (may be null in first week), backing up from first defined day
      if (week[0]) return new Date(week[0]);
      for (let i = 0; i < 7; i++) {
        if (week[i]) {
          const d = new Date(week[i]);
          const offset = (d.getDay() + 6) % 7; // Monday=0
          d.setDate(d.getDate() - offset);
          d.setHours(12, 0, 0, 0);
          return d;
        }
      }
      return null;
    };

    const mondayMonthAt = (weekIndex) => {
      // Special-case: first week of the displayed year should be labeled January
      if (weekIndex === 0) return 0; // January
      const mon = getMonday(weeks[weekIndex]);
      return mon ? mon.getMonth() : null;
    };

    let start = 0;
    let current = mondayMonthAt(0);
    for (let wi = 1; wi < weeks.length; wi++) {
      const m = mondayMonthAt(wi);
      if (m !== current) {
        // close previous header
        monthHeaders.push({ month: current, start, span: wi - start });
        start = wi;
        current = m;
      }
    }
    // close last header
    monthHeaders.push({ month: current, start, span: weeks.length - start });
    return monthHeaders;
  };

  const calendarData = React.useMemo(() => generateCalendarData(year), [year]);
  const monthHeaders = React.useMemo(
    () => getMonthHeaders(calendarData),
    [calendarData]
  );

  // --- Helper and precomputed maps for fast date lookups ---
  const normalizeDateKey = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = dt.getMonth() + 1;
    const day = dt.getDate();
    return `${y}-${String(m).padStart(2, "0")} -${String(day).padStart(
      2,
      "0"
    )}`.replace(/\s+/g, "");
  };

  const competitionDatesMap = React.useMemo(() => {
    const set = new Set();
    (client.competitions || []).forEach((comp) => {
      const key = normalizeDateKey(new Date(comp.date));
      if (key) set.add(key);
    });
    return set;
  }, [client.competitions]);

  const workoutDatesMap = React.useMemo(() => {
    const set = new Set();
    (client.getWorkouts() || []).forEach((w) => {
      const key = normalizeDateKey(new Date(w.date));
      if (key) set.add(key);
    });
    return set;
  }, [client]);

  const getMondayOfWeek = (weekIndex, calendarData, midDay = false) => {
    const week = calendarData[weekIndex];
    if (!week) return null;

    const withDesiredTime = (date) => {
      const d = new Date(date);
      if (midDay) {
        d.setHours(12, 0, 0, 0); // 12:00 local time
      } else {
        d.setHours(0, 0, 0, 0); // 00:00 local time
      }
      return d;
    };

    // If Monday exists in this week array, normalize its time and return
    if (week[0]) return withDesiredTime(week[0]);

    // Otherwise, find the first defined day in the week and back up to Monday
    for (let di = 0; di < 7; di++) {
      if (week[di]) {
        const d = new Date(week[di]);
        // JavaScript getDay(): 0=Sunday,1=Monday... convert so Monday is 0
        const offset = (d.getDay() + 6) % 7; // Monday -> 0, Tuesday->1, Sunday->6
        d.setDate(d.getDate() - offset);
        return withDesiredTime(d);
      }
    }
    return null;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const {
    sortedYearBlocks,
    blockColorMap,
    macroColorMap,
    weekColors: computedWeekColors,
    blockRowSegments,
    macroRowSegments,
    primaryBlockPerWeek,
  } = (() => {
    const sortedBlocks = client.sortedBlocksByDateWeekCalendar;

    // build per-block workout sets for quick lookup
    const blockWorkoutDatesMap = {};
    sortedBlocks.forEach((block) => {
      const set = new Set();
      (block.workouts || []).forEach((w) => {
        const key = normalizeDateKey(new Date(w.date));
        if (key) set.add(key);
      });
      blockWorkoutDatesMap[block.id] = set;
    });

    // overlap test: block effective span (including dated workouts)
    const blockOverlapsWeek = (block, weekIndex) => {
      const weekStart = getMondayOfWeek(weekIndex, calendarData); // 00:00
      if (!weekStart || !block?.initialDate) return false;
      const weekEnd = endOfWeek(weekStart);

      const blockStartMon = getMondayOfWeekDateUtils(block.initialDate, true);
      const blockEndSun = block.endDate(block.effectiveTotalWeeks);

      return blockStartMon <= weekEnd && blockEndSun > weekStart;
    };

    // determine primary block per week: prefer blocks with workouts in that week, otherwise first overlapping block wins
    const indexMap = {};
    sortedBlocks.forEach((b, i) => {
      indexMap[b.id] = i;
    });
    const primaryBlockPerWeek = new Array(calendarData.length).fill(null);
    for (let wi = 0; wi < calendarData.length; wi++) {
      const candidates = sortedBlocks.filter((block) =>
        blockOverlapsWeek(block, wi)
      );
      if (candidates.length === 0) continue;
      // filter those with a workout in this week
      const withWorkout = candidates.filter((block) => {
        const workoutSet = blockWorkoutDatesMap[block.id] || new Set();
        const week = calendarData[wi];
        for (let di = 0; di < 7; di++) {
          const date = week[di];
          if (date) {
            const key = normalizeDateKey(date);
            if (workoutSet.has(key)) return true;
          }
        }
        return false;
      });
      let winner = null;
      if (withWorkout.length > 0) {
        winner = withWorkout.sort((a, b) => indexMap[a.id] - indexMap[b.id])[0];
      } else {
        // fallback to first overlapping block (deterministic by original order)
        winner = candidates.sort((a, b) => indexMap[a.id] - indexMap[b.id])[0];
      }
      primaryBlockPerWeek[wi] = winner;
    }

    // coalesce into resolved non-overlapping yearBlocks
    const resolvedYearBlocks = [];
    let cursorBlock = primaryBlockPerWeek[0];
    let span = 0;
    let start = 0;
    for (let wi = 0; wi < primaryBlockPerWeek.length; wi++) {
      const current = primaryBlockPerWeek[wi];
      if (wi === 0) {
        cursorBlock = current;
        span = 1;
        start = 0;
        continue;
      }
      if (current === cursorBlock) {
        span += 1;
      } else {
        resolvedYearBlocks.push({
          block: cursorBlock,
          start,
          span,
          end: start + span,
        });
        cursorBlock = current;
        start = wi;
        span = 1;
      }
    }
    if (span > 0) {
      resolvedYearBlocks.push({
        block: cursorBlock,
        start,
        span,
        end: start + span,
      });
    }
    const yearBlocks = resolvedYearBlocks;

    // Use shared color mapping from client store (stable across views/years)
    const macroColorMapLocal = client.macroColorMap || {};
    const blockColorMapLocal = client.blockColorMapById || {};

    // weekColors: paint each resolved block span with its block color
    const weekColorsLocal = {};
    yearBlocks.forEach((yb) => {
      if (!yb.block) return;
      const color = blockColorMapLocal[yb.block.id] || "transparent";
      for (let wi = yb.start; wi < yb.end; wi++) {
        weekColorsLocal[wi] = color;
      }
    });
    calendarData.forEach((_, wi) => {
      if (!weekColorsLocal.hasOwnProperty(wi)) {
        weekColorsLocal[wi] = "transparent";
      }
    });

    // blockRowSegments
    const blockRowSegmentsLocal = [];
    let cursor = 0;
    yearBlocks.forEach(({ block, start, span }, idx) => {
      if (start > cursor) {
        blockRowSegmentsLocal.push({
          block: null,
          span: start - cursor,
          key: `gap-${cursor}-${year}`,
        });
      }
      blockRowSegmentsLocal.push({
        block,
        span,
        key: `${block?.id}-${year}-${start}`,
      });
      cursor = start + span;
    });

    // macroRowSegments: merge and absorb gaps between same-label segments
    // distinguish between no-block (null) and empty-label ('')
    const labels = primaryBlockPerWeek.map((b) => (b ? b.label : null));
    // absorb interior gaps between identical labels
    const absorbedLabels = [...labels];
    let scan = 0;
    while (scan < absorbedLabels.length) {
      if (absorbedLabels[scan] === null) {
        let startIdx = scan;
        while (scan < absorbedLabels.length && absorbedLabels[scan] === null) {
          scan++;
        }
        const endIdx = scan;
        const leftLabel = startIdx > 0 ? absorbedLabels[startIdx - 1] : null;
        const rightLabel =
          endIdx < absorbedLabels.length ? absorbedLabels[endIdx] : null;
        if (leftLabel && leftLabel === rightLabel) {
          for (let k = startIdx; k < endIdx; k++) {
            absorbedLabels[k] = leftLabel;
          }
        }
      } else {
        scan++;
      }
    }

    // build macroRowSegmentsLocal
    const macroRowSegmentsLocal = [];
    let segStart = 0;
    let segLabel = absorbedLabels[0];
    for (let i = 1; i <= absorbedLabels.length; i++) {
      if (i === absorbedLabels.length || absorbedLabels[i] !== segLabel) {
        const span = i - segStart;
        const keyLabel = segLabel || "gap";
        macroRowSegmentsLocal.push({
          label: segLabel,
          span,
          start: segStart,
          key: `macro-${keyLabel}-${segStart}`,
        });
        segStart = i;
        segLabel = i < absorbedLabels.length ? absorbedLabels[i] : null;
      }
    }

    return {
      sortedYearBlocks: sortedBlocks,
      blockColorMap: blockColorMapLocal,
      macroColorMap: macroColorMapLocal,
      weekColors: weekColorsLocal,
      blockRowSegments: blockRowSegmentsLocal,
      macroRowSegments: macroRowSegmentsLocal,
      primaryBlockPerWeek,
    };
  })();

  // rename computedWeekColors to weekColors for backwards compatibility
  const weekColors = computedWeekColors;

  // Clamp the selection end so it never crosses a non-transparent week
  // and never exceeds MAX_SELECT_WEEKS in length
  const clampEndWeekWithinTransparent = React.useCallback(
    (start, target) => {
      if (start === null || start === undefined) return target;
      const n = calendarData.length;
      if (target >= start) {
        // cap by max length (start + 23 => 24 weeks)
        let capped = Math.min(target, start + (MAX_SELECT_WEEKS - 1), n - 1);
        for (let i = start + 1; i <= capped && i < n; i++) {
          if (weekColors[i] !== "transparent") return i - 1;
        }
        return capped;
      } else {
        let capped = Math.max(target, start - (MAX_SELECT_WEEKS - 1), 0);
        for (let i = start - 1; i >= capped && i >= 0; i--) {
          if (weekColors[i] !== "transparent") return i + 1;
        }
        return capped;
      }
    },
    [calendarData, weekColors]
  );

  // --- Selection helpers (clean API) ---
  const getClientX = React.useCallback((evt, gestureState) => {
    let x = evt?.nativeEvent?.clientX ?? evt?.nativeEvent?.pageX ?? null;
    if (
      x == null &&
      evt?.nativeEvent?.touches &&
      evt.nativeEvent.touches.length > 0
    ) {
      const t = evt.nativeEvent.touches[0];
      x = t.clientX ?? t.pageX ?? null;
    }
    if (
      x == null &&
      evt?.nativeEvent?.changedTouches &&
      evt.nativeEvent.changedTouches.length > 0
    ) {
      const t = evt.nativeEvent.changedTouches[0];
      x = t.clientX ?? t.pageX ?? null;
    }
    if (x == null && typeof gestureState?.moveX === "number")
      x = gestureState.moveX;
    if (x == null && typeof gestureState?.x0 === "number") x = gestureState.x0;
    return x;
  }, []);

  const getWeekIndexFromClientX = React.useCallback(
    (clientX) => {
      if (clientX == null) return null;
      const container = gridRef.current;
      if (!container || typeof window === "undefined") {
        const idx = Math.floor(clientX / Calendar.DAY_WIDTH);
        return Math.max(0, Math.min(calendarData.length - 1, idx));
      }
      const rect = container.getBoundingClientRect
        ? container.getBoundingClientRect()
        : null;
      const left = rect ? rect.left : 0;
      const x = clientX - left;
      const idx = Math.floor(x / Calendar.DAY_WIDTH);
      return Math.max(0, Math.min(calendarData.length - 1, idx));
    },
    [calendarData]
  );

  const openAddBlockWithSelection = React.useCallback(
    (start, finalEnd) => {
      const monday = getMondayOfWeek(start, calendarData, true);
      if (!monday) return;
      const startDate = new Date(monday);
      setSelectionStartDate(startDate);
      setSelectionStartWeek(start);
      setSelectionEndWeek(finalEnd);
      if (openModalTimeoutRef.current)
        clearTimeout(openModalTimeoutRef.current);
      openModalTimeoutRef.current = setTimeout(
        () => setShowingAddCalendarBlock(true),
        80
      );
    },
    [calendarData]
  );

  // Clear pending timeouts on unmount
  useEffect(() => {
    return () => {
      if (openModalTimeoutRef.current)
        clearTimeout(openModalTimeoutRef.current);
    };
  }, []);

  // Grid-level PanResponder for cross-cell drag selection
  const gridPanResponder = React.useMemo(() => {
    return PanResponder.create({
      onStartShouldSetPanResponder: (evt, gestureState) => {
        // Do not intercept when dragging a block segment
        if (client.blockHoverId != null) return false;
        const clientX = getClientX(evt, gestureState);
        const initialWeek = getWeekIndexFromClientX(clientX);
        const should =
          initialWeek != null &&
          weekColors[initialWeek] === "transparent" &&
          !showingAddCalendarBlock;
        return should;
      },
      onStartShouldSetPanResponderCapture: (evt, gestureState) => {
        if (client.blockHoverId != null) return false;
        const clientX = getClientX(evt, gestureState);
        const initialWeek = getWeekIndexFromClientX(clientX);
        const should =
          initialWeek != null &&
          weekColors[initialWeek] === "transparent" &&
          !showingAddCalendarBlock;
        return should;
      },
      onMoveShouldSetPanResponder: (evt, gestureState) => {
        if (client.blockHoverId != null) return false;
        const absDx = Math.abs(gestureState.dx);
        const should = isSelectingRef.current || absDx > 2;
        return should;
      },
      onMoveShouldSetPanResponderCapture: (evt, gestureState) => {
        if (client.blockHoverId != null) return false;
        const absDx = Math.abs(gestureState.dx);
        const should = isSelectingRef.current || absDx > 2;
        return should;
      },
      onPanResponderGrant: (evt, gestureState) => {
        if (client.blockHoverId != null) return;
        const clientX = getClientX(evt, gestureState);
        const initialWeek = getWeekIndexFromClientX(clientX);
        if (
          initialWeek == null ||
          weekColors[initialWeek] !== "transparent" ||
          showingAddCalendarBlock
        )
          return;

        // Prevent native behaviors on web
        if (evt.preventDefault) evt.preventDefault();
        if (evt.stopPropagation) evt.stopPropagation();

        isSelectingRef.current = true;
        selectionStartRef.current = initialWeek;
        selectionEndRef.current = initialWeek;
        setIsSelecting(true);
        setSelectionStartWeek(initialWeek);
        setSelectionEndWeek(initialWeek);
        const startDate = getMondayOfWeek(initialWeek, calendarData, true);
        setSelectionStartDate(startDate);
      },
      onPanResponderMove: (evt, gestureState) => {
        if (client.blockHoverId != null) return;
        if (!isSelectingRef.current || selectionStartRef.current == null)
          return;
        // Prevent native behaviors on web
        if (evt.preventDefault) evt.preventDefault();
        if (evt.stopPropagation) evt.stopPropagation();

        const clientX = getClientX(evt, gestureState);
        const hoverWeek = getWeekIndexFromClientX(clientX);
        if (hoverWeek == null) return;
        const clamped = clampEndWeekWithinTransparent(
          selectionStartRef.current,
          hoverWeek
        );
        if (clamped !== selectionEndRef.current) {
          selectionEndRef.current = clamped;
          setSelectionEndWeek(clamped);
        }
      },
      onPanResponderTerminationRequest: (evt, gestureState) => {
        const allow = !isSelectingRef.current;
        return allow;
      },
      onPanResponderRelease: (evt, gestureState) => {
        if (client.blockHoverId != null) return; // React DnD handles block drops
        const startIdx = selectionStartRef.current;
        const endIdx = selectionEndRef.current;
        if (!isSelectingRef.current || startIdx == null || endIdx == null) {
          isSelectingRef.current = false;
          setIsSelecting(false);
          return;
        }

        isSelectingRef.current = false;
        setIsSelecting(false);

        const start = Math.min(startIdx, endIdx);
        const end = Math.max(startIdx, endIdx);
        const weekCount = end - start + 1;
        const finalWeekCount = Math.min(weekCount, MAX_SELECT_WEEKS);
        const finalEnd = start + (finalWeekCount - 1);

        openAddBlockWithSelection(start, finalEnd);
      },
      onPanResponderTerminate: (evt, gestureState) => {
        if (client.blockHoverId != null) return;
        isSelectingRef.current = false;
        setIsSelecting(false);
      },
      onShouldBlockNativeResponder: (evt, gestureState) => {
        const shouldBlock = isSelectingRef.current;
        // Prevent native behaviors on web
        if (evt.preventDefault) evt.preventDefault();
        if (evt.stopPropagation) evt.stopPropagation();
        return shouldBlock;
      },
    });
  }, [
    getClientX,
    getWeekIndexFromClientX,
    clampEndWeekWithinTransparent,
    showingAddCalendarBlock,
    weekColors,
    openAddBlockWithSelection,
  ]);

  // maxDroppableWeeks: number of consecutive free weeks including current week (for drag & drop), extended into next year based on first next-year block
  const maxDroppableWeeks = React.useMemo(() => {
    const n = calendarData.length;
    if (n === 0) return [];
    // find first block that starts in the next year
    const nextYear = year + 1;
    const nextYearBlocks = client.blocks
      .filter(
        (b) =>
          b.initialDate && new Date(b.initialDate).getFullYear() === nextYear
      )
      .sort((a, b) => new Date(a.initialDate) - new Date(b.initialDate));
    let weeksUntilNext = 53;
    if (nextYearBlocks.length > 0) {
      // compute Monday of that block's initialDate
      const nextDate = new Date(nextYearBlocks[0].initialDate);
      const nextMon = new Date(nextDate);
      const offset = (nextMon.getDay() + 6) % 7; // Monday=0
      nextMon.setDate(nextMon.getDate() - offset);
      // Monday of last displayed week
      const lastMon = calendarData[n - 1]?.[0];
      const diffMs = nextMon - lastMon;
      weeksUntilNext = Math.round(diffMs / (7 * 86400000)) - 1;
      if (weeksUntilNext < 0) weeksUntilNext = 0;
    }

    const arr = new Array(n).fill(0);
    for (let i = 0; i < n; i++) {
      // if this week has a block, no drop possible
      if (weekColors[i] !== "transparent") {
        arr[i] = 0;
        continue;
      }
      // count free weeks in current year
      let runLen = 1;
      let j = i + 1;
      while (j < n && weekColors[j] === "transparent") {
        runLen++;
        j++;
      }
      // if we reached end of year, add next-year free weeks until first block
      arr[i] = runLen + (j === n ? weeksUntilNext : 0);
    }
    return arr;
  }, [
    weekColors,
    calendarData,
    year,
    client.sortedBlocksByDateWeekCalendar,
    client.blockHoverId,
  ]);

  const DayView = observer(
    ({
      date,
      dayIndex,
      weekIndex,
      isToday,
      hasCompetition,
      hasWorkout,
      weekColor,
      canDrop,
    }) => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <View
          style={[
            styles.dayCell,
            {
              backgroundColor: hasCompetition
                ? Colors.borderColor
                : isToday
                ? Colors.borderColor
                : weekColor || "transparent",
              borderTopWidth: dayIndex === 0 ? 1 : 0,
              borderLeftWidth: weekIndex === 0 ? 1 : 0,
              borderTopLeftRadius: weekIndex === 0 && dayIndex === 0 && 10,
              borderBottomLeftRadius: weekIndex === 0 && dayIndex === 6 && 10,
              borderTopRightRadius:
                weekIndex === calendarData.length - 1 && dayIndex === 0 && 10,
              borderBottomRightRadius:
                weekIndex === calendarData.length - 1 && dayIndex === 6 && 10,
              opacity: isHovered ? 0.5 : 1,
              ...(selectionStartWeek !== null &&
              selectionEndWeek !== null &&
              weekIndex >= Math.min(selectionStartWeek, selectionEndWeek) &&
              weekIndex <= Math.max(selectionStartWeek, selectionEndWeek) &&
              weekColor === "transparent"
                ? { backgroundColor: "rgba(63, 123, 141, 0.50)" }
                : {}),
            },
          ]}
          onMouseEnter={() => {
            if (!isMobile) {
              setIsHovered(true);
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              setIsHovered(false);
            }
          }}
        >
          {hasCompetition ? (
            <Ionicons name="podium" size={18} color={Colors.bubbleColor} />
          ) : (
            <View>
              <Text
                style={[
                  styles.dayText,
                  {
                    color: canDrop
                      ? Colors.bubbleColor
                      : isToday
                      ? "black"
                      : "rgb(99, 99, 99)",
                    fontWeight: canDrop || isToday ? "bold" : "regular",
                  },
                ]}
              >
                {date !== null ? `${date.getDate()}` : ""}
              </Text>
              {hasWorkout && (
                <View
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: "rgba(99, 99, 99,0.7)",
                    alignSelf: "center",
                    marginTop: -2,
                  }}
                />
              )}
            </View>
          )}
        </View>
      );
    }
  );

  const WeekColumn = observer(({ week, weekIndex, maxDroppableWeeks }) => {
    const ITEM_TYPE_BLOCK = "BLOCK_SEGMENT";
    const [{ isOver, canDrop }, dropRef] = useDrop({
      accept: ITEM_TYPE_BLOCK,
      canDrop: (item) =>
        item.span <= maxDroppableWeeks &&
        !item?.block?.workouts?.some((w) => w.date),
      hover: (item, monitor) => {
        if (monitor.isOver({ shallow: true }) && monitor.canDrop()) {
          client.setProperty("blockHoverStart", weekIndex);
          client.setProperty("blockHoverSpan", item.span);
        } else {
          client.setProperty("blockHoverStart", null);
        }
      },
      drop: (item) => {
        console.log(
          "Dropped block",
          item.block,
          "span",
          item.span,
          "at week",
          weekIndex
        );
        if (client.blockHoverStart == null) return; // allow index 0
        const monday = getMondayOfWeek(
          client.blockHoverStart,
          calendarData,
          true
        );
        if (!monday) return;
        item.block.setProperty("calendarStartDate", monday);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    });

    useEffect(() => {
      if (!isOver) {
        client.setProperty("blockHoverStart", null);
      }
    }, [isOver]);

    return (
      <View
        ref={dropRef}
        style={[styles.weekColumn, { width: Calendar.DAY_WIDTH }]}
        key={weekIndex}
      >
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            color: "gray",
            marginBottom: 4,
          }}
        >
          <Text
            style={{
              fontSize: 10,
              opacity: 0.35,
            }}
          >
            {weekIndex + 1}
          </Text>
        </View>
        <View
          style={[
            // highlight if dragging over valid target
            isOver && canDrop && { backgroundColor: "rgba(63,123,141,0.2)" },
            // highlight the full span for drop preview (driven by origin week only)
            client.blockHoverStart !== null &&
              weekIndex >= client.blockHoverStart &&
              weekIndex < client.blockHoverStart + client.blockHoverSpan && {
                backgroundColor: "rgba(63,123,141,0.2)",
              },
          ]}
        >
          {week.map((date, dayIndex) => {
            const isToday = date && isSameDay(new Date(), date);
            const key = date ? normalizeDateKey(date) : "";
            const hasCompetition = date ? competitionDatesMap.has(key) : false;
            const hasWorkout = date ? workoutDatesMap.has(key) : false;

            return (
              <DayView
                key={dayIndex}
                date={date}
                dayIndex={dayIndex}
                weekIndex={weekIndex}
                isToday={isToday}
                hasCompetition={hasCompetition}
                hasWorkout={hasWorkout}
                weekColor={weekColors[weekIndex]}
                canDrop={
                  client.blockHoverId !== null &&
                  client.blockHoverSpan > 0 &&
                  maxDroppableWeeks >= client.blockHoverSpan
                }
              />
            );
          })}
        </View>
      </View>
    );
  });

  /* BLOCK & MACRO ROWS  */
  const blocksWithoutDate = client.blocksWithoutDate;

  const hasAnyBlock = React.useMemo(
    () => blockRowSegments.some((seg) => !!seg.block),
    [blockRowSegments]
  );
  const hasAnyMacro = React.useMemo(
    () => macroRowSegments.some((seg) => !!(seg.label && seg.label !== "")),
    [macroRowSegments]
  );
  const showEmptyState = !hasAnyBlock && !hasAnyMacro;
  const shouldUseCalendarDashboardInteractions = shouldShowProtocolSlotUi(
    client?.coach,
  );

  const onBlockPress = (seg) => {
    if (!shouldUseCalendarDashboardInteractions) {
      client.setProperty("selectedMacro", seg.block.label);
      client.setSelectedBlock(seg.block);
      seg.block.setProperty("isShowingDashboard", false);
      client.setProperty("selectedTab", "planification-string");
      return;
    }

    client.setProperty("selectedMacro", seg.block.label);
    if (client.scrollRef?.scrollTo) {
      const macroBlocks = client.selectedMacroBlocks;
      const indexToScroll = macroBlocks.findIndex((b) => b.id === seg.block.id);
      if (indexToScroll >= 0) {
        let offset = 0;
        for (let i = 0; i < indexToScroll; i++) {
          const block = macroBlocks[i];
          const blockWidth = block.getBlockDashboardWidth;
          offset += blockWidth + 16; // add row gap
        }
        setTimeout(() => {
          client.scrollRef.scrollTo({ x: offset, animated: true });
        }, 50);
      }
    }
  };

  const onMacroPress = (seg) => {
    client.setProperty("selectedMacro", seg.label);
    setTimeout(() => {
      client.scrollRef?.scrollTo({ x: 0, animated: true });
    }, 50);
  };

  const onRenameMacro = (seg, newLabel) => {
    if (seg.label == null) return; // ignore gaps
    // pick a seed block from this segment safely
    let seedBlock = null;
    for (let wi = seg.start; wi < seg.start + seg.span; wi++) {
      const b = primaryBlockPerWeek?.[wi];
      if (b && b.label === seg.label) {
        seedBlock = b;
        break;
      }
    }
    if (!seedBlock) return;

    const ids = client.getContiguousBlockIdsForSeed(seedBlock);

    client.blocks.forEach((b) => {
      if (ids.has(b.id)) {
        b.setProperty("label", newLabel);
        client.updateClientTransactionData(b.id);
      }
    });
    client.startOrResetBatchTimer();

    client.setProperty("selectedMacro", newLabel);
  };

  // /* SCROLL HELPERS */
  const scrollRef = useRef(null);
  const lastOffsetX = useRef(0);

  useEffect(() => {
    if (client.initCalendarScroll) return;
    const today = new Date();
    // Only auto-scroll if we are looking at the current year and it's June (5) or later
    if (today.getFullYear() === year && today.getMonth() >= 5) {
      client.setProperty("initCalendarScroll", true);
      // Defer twice to allow layout pass to finish without relying on onContentSizeChange
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollRef.current?.scrollToEnd({ animated: false });
        });
      });
    }
    // runs once on mount only
  }, []);

  return (
    <View
      style={{
        backgroundColor: Colors.whiteColor,
        paddingVertical: screenSize === "small" ? 8 : 16,
        paddingHorizontal: screenSize === "small" ? 8 : 16,
        borderRadius: 14,
        borderWidth: 0.1,
        borderColor: Colors.borderColor,
      }}
    >
      {!isMobile && (
        <>
          <ScrollButtons
            scrollRef={scrollRef}
            lastOffsetX={lastOffsetX}
            scrollDirection="right"
            width={16}
          />
          <ScrollButtons
            scrollRef={scrollRef}
            lastOffsetX={lastOffsetX}
            scrollDirection="left"
            width={16}
          />
        </>
      )}

      <ScrollView
        showsHorizontalScrollIndicator={false}
        ref={scrollRef}
        horizontal={true}
        scrollEnabled={!isSelecting} // Disable scroll during selection
        onScroll={(e) => {
          lastOffsetX.current = e.nativeEvent.contentOffset.x;
        }}
        scrollEventThrottle={48}
      >
        <View
          style={styles.container}
          onMouseLeave={() => {
            if (!showingAddCalendarBlock && isSelecting) {
              clearSelection();
            }
          }}
        >
          <View style={{ marginRight: 4, alignItems: "flex-start" }}>
            <View style={{ marginLeft: 2 }}>
              <View style={[styles.emptyCell, { marginTop: 15 }]} />
              {daysOfWeek.map((day, index) => (
                <View key={index} style={[styles.dayNameCell]}>
                  <Text
                    style={[styles.dayNameText, { color: Colors.blackColor }]}
                  >
                    {(t(day) || "").charAt(0)}
                  </Text>
                </View>
              ))}
            </View>
            {blocksWithoutDate.length > 0 && (
              <View style={{ marginTop: 22 }}>
                <TouchableOpacity
                  onPress={() =>
                    client.setProperty(
                      "showingBlocksWithoutDate",
                      !client.showingBlocksWithoutDate
                    )
                  }
                >
                  <Ionicons
                    name={
                      !client.showingBlocksWithoutDate
                        ? "add-circle-outline"
                        : "remove-circle-outline"
                    }
                    size={18}
                    color={Colors.bubbleColor}
                    style={{}}
                  />
                </TouchableOpacity>
              </View>
            )}
          </View>
          <View>
            <View style={[styles.headerRow]}>
              {monthHeaders.map((header, index) => (
                <View
                  key={index}
                  style={[
                    styles.headerCell,
                    {
                      width: header.span * Calendar.DAY_WIDTH,
                      backgroundColor:
                        index % 2
                          ? "#e5e7eb" // light neutral gray
                          : "#f3f4f6", // even lighter gray
                      borderTopLeftRadius: index === 0 ? 10 : 0,
                      borderBottomLeftRadius: index === 0 ? 10 : 0,
                      borderTopRightRadius:
                        index === monthHeaders.length - 1 ? 10 : 0,
                      borderBottomRightRadius:
                        index === monthHeaders.length - 1 ? 10 : 0,
                    },
                  ]}
                >
                  <Text style={styles.headerText}>
                    {t(monthNames[header.month])}
                  </Text>
                </View>
              ))}
            </View>
            <View
              ref={gridRef}
              style={styles.calendarRow}
              pointerEvents={client.blockHoverId ? "auto" : "box-only"}
              {...gridPanResponder.panHandlers}
              onContextMenu={(e) => e.preventDefault()}
              onSelectStart={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              onTouchMove={(e) => {
                // While dragging a block on mobile, update hover span based on finger position
                if (!client.blockHoverId) return;
                const touch =
                  e.nativeEvent.touches?.[0] ||
                  e.nativeEvent.changedTouches?.[0];
                if (!touch) return;
                const rect = gridRef.current?.getBoundingClientRect?.();
                if (!rect) return;
                const x = (touch.clientX ?? touch.pageX) - rect.left;
                if (typeof x !== "number") return;
                const weekIndex = Math.max(
                  0,
                  Math.min(
                    calendarData.length - 1,
                    Math.floor(x / Calendar.DAY_WIDTH)
                  )
                );
                // Keep the span the same; only move preview start
                client.setProperty("blockHoverStart", weekIndex);
              }}
            >
              {calendarData.map((week, weekIndex) => (
                <WeekColumn
                  key={weekIndex}
                  week={week}
                  weekIndex={weekIndex}
                  maxDroppableWeeks={maxDroppableWeeks[weekIndex]}
                />
              ))}
            </View>
            <View style={{ gap: 12 }}>
              {/* BLOCK ROWS */}
              <View
                style={{
                  marginTop: 12,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                {blocksWithoutDate.length > 0 && showEmptyState ? (
                  <View
                    style={{
                      height: Calendar.DAY_WIDTH,
                      alignSelf: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      textAlignVertical: "center",
                    }}
                  >
                    <Text>
                      {t(
                        client.showingBlocksWithoutDate
                          ? "hideBlocksWithoutDate-string"
                          : "showBlocksWithoutDate-string"
                      )}
                    </Text>
                  </View>
                ) : (
                  blockRowSegments.map((seg) => (
                    <CalendarBlockRowView
                      key={seg.key}
                      seg={seg}
                      blockColorMap={blockColorMap}
                      onBlockPress={onBlockPress}
                    />
                  ))
                )}
              </View>
              {/* MACRO ROWS */}
              {hasAnyMacro && (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {macroRowSegments.map((seg) => (
                    <MacroRowView
                      key={seg.key}
                      seg={seg}
                      macroColorMap={macroColorMap}
                      onMacroPress={onMacroPress}
                      onRenameMacro={onRenameMacro}
                      isRowClickable={shouldUseCalendarDashboardInteractions}
                    />
                  ))}
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
      {showEmptyState && (
        <IconEmptyView
          iconLibrary={"MaterialCommunityIcons"}
          mainText={t("noBlocksThisYear-string")}
          subText={t("dragToCreateBlock-string")}
          iconName={"gesture-swipe-right"}
          marginTop={-8}
          marginBottom={16}
        />
      )}

      {client.showingMoveBlockWithWorkoutsWarning && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginTop: 12,
          }}
        >
          <Ionicons
            name="information-circle-outline"
            size={20}
            color={Colors.bubbleColor}
          />
          <Text>{t("cantMoveBlockWithDatedWorkouts-string")}</Text>
        </View>
      )}

      {showingAddCalendarBlock && (
        <AddCalendarBlockView
          isVisible={showingAddCalendarBlock}
          onClose={() => {
            setShowingAddCalendarBlock(false);
            setSelectionStartWeek(null);
            setSelectionEndWeek(null);
            setSelectionStartDate(null);
          }}
          client={client}
          totalWeeks={
            selectionStartWeek !== null && selectionEndWeek !== null
              ? Math.min(
                  Math.abs(selectionEndWeek - selectionStartWeek) + 1,
                  24
                )
              : 1
          }
          startDate={selectionStartDate}
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
  },
  emptyCell: {
    justifyContent: "center",
    alignItems: "center",
    height: 27,
    borderColor: "#ddd",
    marginBottom: 5,
  },
  headerRow: {
    flexDirection: "row",
    borderColor: "#ddd",
    height: 24,
    marginBottom: 8,
  },
  headerCell: {
    justifyContent: "center",
    alignItems: "center",
    borderColor: "#ddd",
  },
  headerText: {
    fontSize: 12,
    fontWeight: "medium",
  },
  calendarRow: {
    flexDirection: "row",
    // Prevent native behaviors while dragging at grid level
    touchAction: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    // Prevent scroll chaining to parent ScrollView on mobile web
    overscrollBehavior: "contain",
    overscrollBehaviorX: "contain",
  },
  weekColumn: {
    flexDirection: "column",
    borderColor: "#ddd",
  },
  dayCell: {
    height: Calendar.DAY_WIDTH,
    width: Calendar.DAY_WIDTH,
    justifyContent: "center",
    alignItems: "center",
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: "#ddd",
    // Improve touch responsiveness on mobile and prevent native web behaviors
    touchAction: "none", // Changed from "manipulation" to "none" to prevent all native gestures
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    // Prevent dragging and context menus
    MozUserSelect: "none",
    msUserSelect: "none",
    WebkitUserDrag: "none",
    KhtmlUserSelect: "none",
  },
  dayText: {
    fontSize: 12,
    padding: 3,
  },
  dayNameCell: {
    height: Calendar.DAY_WIDTH,
    justifyContent: "center",
    alignItems: "center",
    borderColor: "#ddd",
  },
  dayNameText: {
    fontSize: 12,
    paddingRight: 8,
    fontWeight: "medium",
  },
});

export default WeekCalendar;
